import React, { useState, useRef, useEffect } from 'react';
import { Facebook, Linkedin, Instagram, Menu, X, ChevronRight, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import EnrollModal from './EnrollModal';

const NAV_ITEMS = [
  {
    label: 'Services',
    items: [
      { label: 'AI & Machine Learning' },
      { label: 'SAP Services' },
       { label: 'Cloud & DevOps' },
       { label: 'Cyber Security' },
       { label: 'Data Analytics' },
       { label: 'Product Development' },
     ],
  },
  {
    label: 'Solutions',
    items: [
      { label: 'Business Process Outsourcing (BPO)' },
      { label: 'IT Consulting' },
      { label: 'Strategic Staffing' },
      { label: 'Fulltime Placement' },
      { label: 'Direct Sourcing' },
      { label: 'Contract Positions' }
    ],
  },
  {
    label: 'Industries',
    items: [
      { label: 'Insurance Industry' },
      { label: 'Energy & Utilities' },
      { label: 'Banking & Finance Industry' },
      { label: 'Retail' },
      { label: 'Healthcare' },
      { label: 'Telecommunications' },
    ],
  },
  {
    label: 'Technologies',
    items: [
      { label: 'SAP' },
      { label: 'Big Data Analytics' },
      { label: 'Digital Analytics' },
      { label: 'Cyber Security' },
      { label: 'AI (Artificial Intelligence)' },
      { label: 'Cloud Computing' },
      { label: 'Testing' },
    ],
  },
  {
    label: 'Careers',
    items: [],
  },
  {
    label: 'About Us',
    items: [
      { label: 'Teams' },
      { label: 'Company Overview' },
      { label: 'Milestones' },
      { label: 'Contact Us' },
    ],
  },
];

const XBrandIcon = ({ className = 'w-4 h-4' }) => (
  <span className={`${className} inline-flex items-center justify-center font-black leading-none`}>X</span>
);

export default function Header({ activeTab, setActiveTab, setScrollTarget }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [showEnroll, setShowEnroll] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileSub, setActiveMobileSub] = useState(null);
  const closeTimer = useRef(null);

  // Close mobile menu on body scroll or resize
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleMouseEnter = (label) => {
    if (window.innerWidth < 1280) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };

  const handleMouseLeave = () => {
    if (window.innerWidth < 1280) return;
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  const handleNavClick = (item) => {
    if (window.innerWidth < 1280) {
      if (item.items.length === 0) {
        setIsMobileMenuOpen(false);
        handleTabSwitch(item.label);
      } else {
        setActiveMobileSub(activeMobileSub === item.label ? null : item.label);
      }
    } else {
      // Desktop: Always navigate to the overview page
      setOpenMenu(null);
      handleTabSwitch(item.label);
    }
  };

  const handleTabSwitch = (label) => {
    const labelLower = label.toLowerCase();
    if (labelLower === 'careers') setActiveTab('candidate');
    else if (labelLower === 'services') setActiveTab('services');
    else if (labelLower === 'solutions') setActiveTab('solutions');
    else if (labelLower === 'industries') setActiveTab('industries');
    else if (labelLower === 'technologies') setActiveTab('technologies');
    else if (labelLower === 'about us') setActiveTab('about');
    else {
      setActiveTab('home');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubItemClick = (label, subItem) => {
    setOpenMenu(null);
    setIsMobileMenuOpen(false);
    const labelLower = label.toLowerCase();

    if (labelLower === 'services') {
      let targetTab = '';
      if (subItem.includes('AI')) targetTab = 'service-ai';
      else if (subItem.includes('SAP')) targetTab = 'service-sap';
      else if (subItem.includes('DevOps')) targetTab = 'service-cloud-devops';
      else if (subItem.includes('Product')) targetTab = 'service-product-development';
      else if (subItem.includes('Cyber')) targetTab = 'service-cybersecurity';
      else if (subItem.includes('Data Analytics')) targetTab = 'service-data-analytics';

      if (targetTab) {
        setActiveTab(targetTab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (labelLower === 'solutions') {
      let targetTab = '';
      if (subItem.includes('BPO')) targetTab = 'solution-bpo';
      else if (subItem.includes('IT Consulting')) targetTab = 'solution-it';
      else if (subItem.includes('Strategic Staffing')) targetTab = 'solution-contingent';
      else if (subItem.includes('Fulltime')) targetTab = 'solution-fulltime';
      else if (subItem.includes('Direct')) targetTab = 'solution-direct';
      else if (subItem.includes('Contract')) targetTab = 'solution-contract';

      if (targetTab) {
        setActiveTab(targetTab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (labelLower === 'industries') {
      let targetTab = '';
      if (subItem.includes('Insurance')) targetTab = 'industry-insurance';
      else if (subItem.includes('Energy')) targetTab = 'industry-energy';
      else if (subItem.includes('Banking')) targetTab = 'industry-banking';
      else if (subItem.includes('Retail')) targetTab = 'industry-retail';
      else if (subItem.includes('Healthcare')) targetTab = 'industry-healthcare';
      else if (subItem.includes('Telecommunications')) targetTab = 'industry-telecom';

      if (targetTab) {
        setActiveTab(targetTab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setActiveTab('industries');
      }
    } else if (labelLower === 'technologies') {
      let targetTab = '';
      if (subItem === 'SAP') targetTab = 'technology-sap';
      else if (subItem === 'Big Data Analytics') targetTab = 'technology-big-data';
      else if (subItem === 'Digital Analytics') targetTab = 'technology-digital-analytics';
      else if (subItem === 'Cyber Security') targetTab = 'technology-cybersecurity';
      else if (subItem === 'AI (Artificial Intelligence)') targetTab = 'technology-ai';
      else if (subItem === 'Cloud Computing' || subItem === 'AWS' || subItem === 'Azure') targetTab = 'technology-cloud-computing';
      else if (subItem === 'Testing') targetTab = 'technology-testing';

      if (targetTab) {
        setActiveTab(targetTab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setActiveTab('technologies');
      }
    } else if (labelLower === 'about us') {
      let targetTab = '';
      if (subItem === 'Teams') targetTab = 'about-teams';
      else if (subItem === 'Company Overview') targetTab = 'about-company-overview';
      else if (subItem === 'Milestones') targetTab = 'about-milestones';
      else if (subItem === 'Contact Us') targetTab = 'about-contact-us';

      if (targetTab) {
        setActiveTab(targetTab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setActiveTab('about');
      }
    }
  };

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)]">
        <div className="w-full h-20 lg:h-24 px-6 lg:px-12 flex items-center justify-between xl:grid xl:grid-cols-[1fr_auto_1fr]">
          
          {/* Left: Logo Section */}
          <div className="flex justify-start items-center">
            <div
              className="group cursor-pointer flex items-center"
              onClick={() => { 
                setActiveTab('home'); 
                setIsMobileMenuOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' }); 
              }}
            >
              <img 
                src="/top-bar-logo.png" 
                alt="Libsys Inc Logo" 
                className="h-12 lg:h-16 transition-transform duration-500 group-hover:scale-105" 
              />
            </div>
          </div>

          {/* Center: Elite Nav Section (Hidden on xl) */}
          <div className="hidden xl:flex items-center justify-center">
            <div className="flex items-center space-x-1">
              {NAV_ITEMS.map((item) => {
                const hasDropdown = item.items.length > 0;
                const itemLabelLower = item.label.toLowerCase();
                const isActive =
                  (item.label === 'Careers' && activeTab === 'candidate') ||
                  (itemLabelLower === 'about us' && activeTab === 'about') ||
                  (activeTab === itemLabelLower) ||
                  (activeTab.startsWith('service-') && itemLabelLower === 'services') ||
                  (activeTab.startsWith('solution-') && itemLabelLower === 'solutions');

                return (
                  <div
                    key={item.label}
                    className="relative px-1"
                    onMouseEnter={() => handleMouseEnter(item.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      onClick={() => handleNavClick(item)}
                      className={`relative px-4 py-8 text-[15.5px] font-extrabold tracking-tight transition-all duration-300 flex items-center hover:text-red-500 rounded-md focus:outline-none ${
                        isActive ? 'text-primary' : 'text-slate-700'
                      }`}
                    >
                      {item.label}
                      {isActive && (
                        <motion.div 
                          layoutId="navUnderline"
                          className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary rounded-t-full"
                          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        />
                      )}
                    </button>

                    <AnimatePresence>
                      {hasDropdown && openMenu === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 5, scale: 0.98 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-[85%] left-1/2 -translate-x-1/2 w-64 pt-2 z-50"
                        >
                          <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100 py-3 overflow-hidden backdrop-blur-xl">
                            <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-slate-100 rotate-45" />
                            <div className="relative z-10 max-h-[70vh] overflow-y-auto">
                              {item.items.map((subItem) => (
                                <div key={subItem.label} className="relative group/sub">
                                  <button
                                    onClick={() => handleSubItemClick(item.label, subItem.label)}
                                    className="w-full text-left px-5 py-3 text-[14px] font-bold text-slate-700 hover:text-red-500 hover:bg-slate-50 transition-colors flex items-center justify-between"
                                  >
                                    {subItem.label}
                                    {subItem.subItems && <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover/sub:translate-x-1 transition-transform" />}
                                  </button>
                                  {subItem.subItems && (
                                    <div className="absolute left-full top-0 ml-1 w-60 bg-white rounded-2xl shadow-2xl border border-slate-100 py-2 opacity-0 invisible group-hover/sub:opacity-100 group-hover/sub:visible transition-all duration-300">
                                      {subItem.subItems.map(nestedItem => (
                                        <button
                                          key={nestedItem.label}
                                          onClick={() => handleSubItemClick(item.label, nestedItem.label)}
                                          className="w-full text-left px-5 py-2.5 text-[13.5px] font-semibold text-slate-700 hover:text-red-500 hover:bg-slate-50 transition-colors"
                                        >
                                          {nestedItem.label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Controls Section */}
          <div className="flex items-center space-x-4 lg:space-x-6">
            <div className="hidden lg:flex items-center space-x-1">
              {[
                { icon: <Facebook className="w-4 h-4" />, href: 'https://www.facebook.com/people/Libsys-Inc/100066298346924/#', color: 'hover:bg-[#1877f2]' },
                { icon: <XBrandIcon className="w-4 h-4" />, href: 'https://x.com/libsysinc', color: 'hover:bg-black' },
                { icon: <Linkedin className="w-4 h-4" />, href: 'https://www.linkedin.com/company/libsysinc/', color: 'hover:bg-[#0a66c2]' },
                { icon: <Instagram className="w-4 h-4" />, href: 'https://www.instagram.com/libsysinc', color: 'hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7]' }
              ].map((s, idx) => (
                <a 
                  key={idx}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-slate-500 transition-all duration-300 ${s.color} hover:text-white hover:shadow-lg`}
                >
                  {s.icon}
                </a>
              ))}
            </div>

            <div className="h-8 w-[1px] bg-slate-200 hidden xl:block" />

            <button
              onClick={() => setShowEnroll(true)}
              className="hidden md:flex px-8 py-3 bg-primary hover:bg-secondary text-white font-black text-[13px] uppercase tracking-wider rounded-xl transition-all duration-300 shadow-md whitespace-nowrap active:scale-95"
            >
              Enroll Now
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden p-2 text-slate-600 hover:text-red-500 transition-colors focus:outline-none"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[-1]"
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="absolute top-full left-0 w-full h-[calc(100vh-80px)] bg-white border-t border-slate-100 flex flex-col overflow-hidden shadow-2xl z-50"
              >
                <div className="flex-1 overflow-y-auto px-6 py-8 space-y-2">
                  {NAV_ITEMS.map((item) => {
                    const hasDropdown = item.items.length > 0;
                    const isSubOpen = activeMobileSub === item.label;

                    return (
                      <div key={item.label} className="border-b border-slate-50 last:border-0 pb-2 mb-2">
                        <button
                          onClick={() => handleNavClick(item)}
                          className="w-full flex items-center justify-between py-3 text-[18px] font-black text-primary"
                        >
                          {item.label}
                          {hasDropdown && (
                            <motion.div
                              animate={{ rotate: isSubOpen ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            </motion.div>
                          )}
                        </button>

                        <AnimatePresence>
                          {hasDropdown && isSubOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden bg-slate-50/50 rounded-xl px-4 space-y-1 mb-4"
                            >
                              {item.items.map((subItem) => (
                                <div key={subItem.label} className="py-1">
                                  <button
                                    onClick={() => {
                                      if (subItem.subItems) { /* nested mobile support can be added if needed, currently flat for subitems */ }
                                      handleSubItemClick(item.label, subItem.label);
                                    }}
                                    className="w-full text-left py-2.5 text-[15px] font-bold text-slate-600 hover:text-red-500"
                                  >
                                    {subItem.label}
                                  </button>
                                  {subItem.subItems && (
                                    <div className="ml-4 border-l-2 border-slate-200 pl-4 space-y-1">
                                      {subItem.subItems.map(nested => (
                                        <button
                                          key={nested.label}
                                          onClick={() => handleSubItemClick(item.label, nested.label)}
                                          className="w-full text-left py-2 text-[14px] font-semibold text-slate-500"
                                        >
                                          {nested.label}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                  
                  <div className="pt-8 flex flex-col space-y-4">
                    <button
                      onClick={() => { setShowEnroll(true); setIsMobileMenuOpen(false); }}
                      className="w-full py-4 bg-primary text-white font-black text-[15px] uppercase tracking-widest rounded-xl shadow-lg"
                    >
                      Enroll Now
                    </button>
                    
                    <div className="flex justify-center space-x-6 pt-4">
                       {[
                        { icon: <Facebook className="w-5 h-5" />, href: 'https://www.facebook.com/people/Libsys-Inc/100066298346924/#', color: 'hover:bg-[#1877f2]' },
                        { icon: <XBrandIcon className="w-5 h-5" />, href: 'https://x.com/libsysinc', color: 'hover:bg-black' },
                        { icon: <Linkedin className="w-5 h-5" />, href: 'https://www.linkedin.com/company/libsysinc/', color: 'hover:bg-[#0a66c2]' },
                        { icon: <Instagram className="w-5 h-5" />, href: 'https://www.instagram.com/libsysinc', color: 'hover:bg-gradient-to-tr hover:from-[#f9ce34] hover:via-[#ee2a7b] hover:to-[#6228d7]' }
                      ].map((s, idx) => (
                        <a 
                          key={idx} 
                          href={s.href} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={`w-11 h-11 rounded-full flex items-center justify-center bg-slate-100 text-slate-600 transition-all duration-300 ${s.color} hover:text-white hover:shadow-lg`}
                        >
                          {s.icon}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>

      {/* Spacing for fixed header */}
      <div className="h-20" />

      <EnrollModal isOpen={showEnroll} onClose={() => setShowEnroll(false)} />
    </>
  );
}
